import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";
import { LAST_UPDATED, LEGAL_ENTITY, TERMS_VERSION } from "@/lib/legal";

const TermsOfService = () => {
  const { t, lang } = useLanguage();
  useLocalizedRoute();

  useSEO({
    title: { es: t("seo", "termsTitle"), en: t("seo", "termsTitle") },
    description: { es: t("seo", "termsDesc"), en: t("seo", "termsDesc") },
    path: lang === "es" ? "/condiciones-contratacion" : "/en/terms",
    pathEs: "/condiciones-contratacion",
    pathEn: "/en/terms",

  });

  const es = lang === "es";
  const updated = es ? LAST_UPDATED.es : LAST_UPDATED.en;
  const issuer = `${LEGAL_ENTITY.name} — ${LEGAL_ENTITY.address}`;

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-6 pb-20">
        <section className="container max-w-2xl">
          {es ? (
            <article className="prose prose-lg max-w-none text-foreground font-body leading-relaxed">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                {updated} · v{TERMS_VERSION}
              </p>
              <h1 className="font-display text-4xl font-bold mb-8">Condiciones de Contratación y Términos de Servicio</h1>

              <h2>1. Identificación y ámbito</h2>
              <p>
                El servicio comercializado bajo la marca <strong>Retail Lemon</strong> es operado por
                <strong> WP Media LLC</strong>, sociedad de responsabilidad limitada (Limited Liability Company)
                constituida bajo las leyes del <strong>Estado de Delaware, Estados Unidos de América</strong>, con
                domicilio en {LEGAL_ENTITY.address} (en adelante, la <strong>"Compañía"</strong>).
              </p>
              <p>
                Los servicios están dirigidos exclusivamente a profesionales, autónomos, empresas y entidades jurídicas
                que contraten en el marco de su actividad económica. Se trata, por tanto, de un servicio estrictamente
                <strong> Business to Business (B2B)</strong>. Al aceptar estas Condiciones, el Cliente declara que actúa
                como profesional o empresa y no como consumidor final.
              </p>

              <h2>2. Servicio internacional y fiscalidad</h2>
              <p>
                Los servicios son <strong>servicios digitales prestados de forma remota desde Estados Unidos</strong> a
                clientes empresariales de cualquier país, incluidos, a título de ejemplo, mercados de América, Europa y
                resto del mundo. La Compañía no dispone de establecimiento permanente ni representación fiscal en el
                país del Cliente.
              </p>
              <p>
                Las facturas se emiten desde Estados Unidos <strong>sin impuestos locales del país del Cliente</strong>.
                Es responsabilidad exclusiva del Cliente cumplir con sus obligaciones fiscales locales, incluida, cuando
                proceda, la autoliquidación del impuesto por importación de servicios o el mecanismo equivalente en su
                jurisdicción (por ejemplo, la inversión del sujeto pasivo en la Unión Europea). El Cliente es
                responsable de la veracidad de sus datos fiscales y de su número de identificación tributaria.
              </p>


              <h2>3. Planes y condiciones de cada modalidad</h2>
              <h3>3.1 Acceso mediante suscripción</h3>
              <p>
                El acceso a la plataforma se realiza exclusivamente mediante uno de los tres planes de suscripción
                vigentes. No existe un plan gratuito permanente; la Compañía puede habilitar o retirar en el futuro
                modalidades gratuitas, que se regirían por las condiciones publicadas en ese momento.
              </p>

              <h3>3.2 Planes de pago: Básico, Club Pro y Avanzado</h3>
              <ul>
                <li>
                  <strong>Básico — 39 €/mes</strong> (anual: 32,50 €/mes, 390 € al año): 30 consultas al mes al Agente
                  Retail IA, guías, listado de subvenciones y música libre de derechos limitada.
                </li>
                <li>
                  <strong>Club Pro — 69 €/mes</strong> (anual: 57,50 €/mes, 690 € al año): todo lo de Básico, 60 consultas
                  al mes, hoja de ruta, KPIs, detector completo, subvenciones que encajan, casos y soluciones y 4
                  generaciones de anuncios con IA al mes.
                </li>
                <li>
                  <strong>Avanzado — 99 €/mes</strong> (anual: 82,50 €/mes, 990 € al año): todo lo de Club Pro, 100
                  consultas al mes, alta en Google Business, módulos premium y 12 generaciones de anuncios con IA al mes.
                </li>
                <li>
                  <strong>Modalidad anual</strong>: se abona por adelantado el importe de 10 mensualidades por 12 meses de
                  servicio (equivalente a 2 meses gratis). El precio mensual mostrado en la modalidad anual es orientativo;
                  el importe cobrado es el total anual.
                </li>
                <li>
                  <strong>Prueba gratuita de 7 días</strong> en los tres planes, tanto en modalidad mensual como anual
                  (una prueba por persona o negocio). Se solicita un método de pago al inicio, pero el cobro el día del
                  alta es de 0 €. Si el Cliente no cancela durante la prueba, el importe completo del plan y la
                  periodicidad elegidos se cobra automáticamente el <strong>día 8</strong>.
                </li>
                <li>
                  <strong>Sin permanencia</strong>: cancelable en 1 clic en cualquier momento desde el panel; la baja
                  surte efecto al final del periodo ya pagado (mes o año). No se reembolsan periodos ya facturados.
                </li>
              </ul>

              <h3>3.3 Extras (de pago, aparte)</h3>
              <ul>
                <li>
                  Mentores, tramitación de licencias, consulta con abogado, servicio de SEO, música comercial y aroma
                  <strong> no están incluidos</strong> en ningún plan. Se contratan como extras de pago desde el plan
                  Avanzado y su precio se muestra antes de pagar.
                </li>
                <li>Ningún plan incluye consultas ilimitadas con abogado ni música comercial.</li>
                <li>Las sesiones con mentor son en remoto por videoconferencia; las ausencias o cancelaciones con menos de 24 horas de antelación consumen la hora reservada.</li>
              </ul>

              <h3>3.4 Módulos adicionales, horas extra y servicios puntuales</h3>
              <ul>
                <li>Se contratan mediante pago único y quedan disponibles inmediatamente tras el cobro.</li>
                <li>
                  Por su naturaleza de contenido digital de acceso inmediato, <strong>no son reembolsables</strong> una
                  vez ejecutado el cargo.
                </li>
              </ul>

              <h2>4. Presupuestos y servicios personalizados</h2>
              <p>
                Los presupuestos emitidos a través del panel (búsqueda de proveedores, mobiliario, packaging,
                rotulación, obra, producción o cualquier servicio a medida) corresponden a
                <strong> trabajos personalizados y confeccionados conforme a las especificaciones del Cliente</strong>.
              </p>
              <ul>
                <li>
                  La aceptación del presupuesto en el panel constituye una <strong>orden firme, vinculante e
                  irrevocable</strong> desde el momento de la aceptación.
                </li>
                <li>
                  El Cliente solicita y consiente expresamente el <strong>inicio inmediato de la ejecución</strong> y
                  renuncia a cualquier derecho de desistimiento, cancelación o reembolso, total o parcial, respecto de
                  servicios personalizados.
                </li>
                <li>
                  Los plazos indicados son <strong>estimativos y no esenciales</strong>; dependen de terceros
                  proveedores, fabricación, transporte y aduanas.
                </li>
                <li>
                  Los precios de terceros pueden variar hasta el momento de la aceptación; una vez aceptado, el importe
                  queda fijado en los términos del presupuesto.
                </li>
                <li>
                  Las garantías de producto son las que ofrezca el fabricante o proveedor final. La Compañía actúa como
                  intermediaria en la búsqueda y coordinación, salvo que el presupuesto indique expresamente lo
                  contrario.
                </li>
              </ul>
              <p>
                La aceptación se registra electrónicamente con fecha, hora, versión de estas Condiciones y datos
                técnicos del dispositivo, y constituye prueba suficiente del consentimiento del Cliente.
              </p>

              <h2>5. Naturaleza del servicio: sin asesoramiento regulado</h2>
              <p>
                Los contenidos, módulos, plantillas, checklists, calculadoras, diagnósticos, informes y sesiones de
                mentoría tienen finalidad <strong>formativa y de orientación empresarial general</strong>.
              </p>
              <p>
                <strong>No constituyen asesoramiento legal, fiscal, contable, laboral, financiero, de inversión,
                técnico, urbanístico, sanitario ni de seguridad</strong>, ni sustituyen la intervención de un
                profesional habilitado en el país del Cliente. Las cifras, márgenes, previsiones y escenarios que
                genera la plataforma son estimaciones basadas en los datos que introduce el Cliente.
              </p>
              <p>
                Toda decisión empresarial y sus consecuencias son responsabilidad exclusiva del Cliente. La Compañía
                <strong> no garantiza resultados económicos</strong>, incremento de ventas ni rentabilidad.
              </p>
              <p>
                La información sobre ayudas y subvenciones que pueda facilitarse a través de la plataforma o por email
                tiene <strong>carácter meramente informativo</strong>. El Cliente debe verificarla siempre en las fuentes
                oficiales correspondientes (bases, plazos y requisitos). Retail Lemon no garantiza la obtención de
                ninguna subvención, licencia ni ayuda.
              </p>


              <h2>6. Herramientas de inteligencia artificial</h2>
              <p>
                La plataforma incorpora funcionalidades basadas en inteligencia artificial (asistente conversacional,
                tips diarios, resúmenes de evolución, informes y análisis automáticos).
              </p>
              <ul>
                <li>
                  Las respuestas se generan automáticamente y <strong>pueden contener errores, imprecisiones u
                  información desactualizada</strong>. El Cliente debe verificarlas antes de actuar.
                </li>
                <li>
                  El contenido generado por IA <strong>no constituye asesoramiento profesional</strong> ni una
                  declaración vinculante de la Compañía, y no genera derechos ni obligaciones contractuales.
                </li>
                <li>
                  El asistente no facilita proveedores, contactos externos ni precios de mercado de terceros; para ello
                  el Cliente debe usar la sección de Proveedores, gestionada por el equipo humano.
                </li>
                <li>
                  El Cliente se compromete a no introducir en el chat datos personales sensibles, datos de terceros sin
                  base legítima ni información confidencial ajena.
                </li>
                <li>
                  Las conversaciones se almacenan y pueden ser revisadas por el equipo para soporte, control de calidad
                  y mejora del servicio, conforme a la Política de Privacidad.
                </li>
              </ul>

              <h2>7. Contenido del Cliente</h2>
              <p>
                El Cliente conserva la titularidad de la información que sube a la plataforma (fotografías del local,
                documentos, planos, datos económicos, notas, mensajes y archivos adjuntos) y declara y garantiza que
                dispone de los derechos necesarios sobre ella.
              </p>
              <ul>
                <li>
                  El Cliente concede a la Compañía una licencia limitada, no exclusiva y revocable para alojar,
                  procesar y mostrar dicho contenido con la única finalidad de prestar el servicio contratado, generar
                  diagnósticos y gestionar presupuestos y búsquedas de proveedores.
                </li>
                <li>
                  La Compañía podrá utilizar datos <strong>agregados y anonimizados</strong>, que no permitan
                  identificar al Cliente, para estadísticas y mejora del producto.
                </li>
                <li>
                  El Cliente se abstendrá de subir contenido ilícito, difamatorio, que infrinja derechos de terceros o
                  que contenga malware.
                </li>
                <li>
                  La Compañía no garantiza la conservación indefinida de archivos ni actúa como sistema de copia de
                  seguridad. El Cliente debe mantener sus propias copias.
                </li>
              </ul>

              <h2>8. Propiedad intelectual y licencia de uso</h2>
              <p>
                La plataforma, la metodología, los módulos, plantillas, checklists, informes y demás materiales son
                propiedad de la Compañía. El Cliente recibe una <strong>licencia personal, intransferible y limitada</strong>
                para su uso interno en su propio negocio durante la vigencia de la suscripción.
              </p>
              <p>
                Queda expresamente prohibida la reventa, cesión, publicación, redistribución, uso formativo frente a
                terceros, así como el uso de los contenidos para entrenar o alimentar modelos de inteligencia
                artificial, o la extracción automatizada de datos (scraping).
              </p>

              <h2>9. Pagos, reembolsos y renovación automática</h2>
              <h3>9.1 Entidad operadora y jurisdicción</h3>
              <p>
                Los servicios digitales, herramientas software y programas de mentoría son prestados y facturados por la
                entidad corporativa estadounidense <strong>WP Media LLC</strong>. La contratación se rige por las
                condiciones aquí expuestas y por la legislación aplicable a transacciones comerciales internacionales.
              </p>

              <h3>9.2 Facturación mensual o anual, prueba y cancelación</h3>
              <p>
                Los planes se facturan <strong>mensual o anualmente</strong>, según elija el Cliente, y <strong>sin
                permanencia</strong>. Básico, Club Pro y Avanzado incluyen una <strong>prueba gratuita de 7 días</strong>
                en ambas periodicidades (una por persona o negocio); antes de confirmar se muestra el importe total que
                se cobrará. Si el usuario no cancela durante la prueba, el primer cobro se realiza el <strong>día
                8</strong>. Puede cancelar en cualquier momento desde el panel; la baja surte efecto al final del periodo
                ya pagado, manteniendo el acceso hasta entonces.
              </p>

              <h3>9.3 Régimen general de pagos</h3>
              <p>
                Todos los pagos se procesan a través de Stripe y se entienden aceptados de forma definitiva en el
                momento de su cobro. <strong>Todas las ventas son finales y no reembolsables ("all sales are
                final")</strong>. Al tratarse de un servicio digital B2B operado bajo legislación estadounidense, no
                resulta de aplicación el derecho de desistimiento de catorce (14) días previsto en la normativa europea
                de consumo.
              </p>
              <p>
                Las suscripciones se renuevan automáticamente cargando el importe vigente al método de pago
                registrado. Es responsabilidad exclusiva del Cliente cancelar desde su panel
                (<strong>panel.retaillemon.com</strong>) antes de la fecha de corte. El impago habilita a la Compañía a
                suspender el acceso sin previo aviso, sin perjuicio de la deuda pendiente.
              </p>


              <h2>10. Acumulación y caducidad de horas de mentoría</h2>
              <ul>
                <li><strong>Asignación mensual:</strong> las horas incluidas en cada plan se asignan al inicio del ciclo de facturación.</li>
                <li><strong>Rollover:</strong> las horas no consumidas se acumulan, como cortesía comercial, al ciclo inmediatamente posterior.</li>
                <li><strong>Caducidad:</strong> las horas acumuladas tienen una vigencia máxima de un (1) ciclo adicional; transcurrido el mismo, caducan definitivamente.</li>
                <li><strong>Sin reembolso:</strong> las horas no consumidas o caducadas no serán reembolsadas ni canjeadas por otros servicios.</li>
              </ul>

              <h2>11. Disponibilidad, limitación de responsabilidad y fuerza mayor</h2>
              <p>
                El servicio se presta <strong>"tal cual" y "según disponibilidad"</strong>, sin garantía de
                funcionamiento ininterrumpido ni libre de errores, y sin acuerdo de nivel de servicio (SLA) salvo pacto
                escrito específico.
              </p>
              <p>
                En la máxima medida permitida por la ley aplicable, la <strong>responsabilidad total y acumulada</strong>
                de la Compañía frente al Cliente por cualquier concepto queda limitada a los importes efectivamente
                abonados por el Cliente en los <strong>tres (3) meses</strong> anteriores al hecho que motive la
                reclamación.
              </p>
              <p>
                Queda excluida toda responsabilidad por daños indirectos, lucro cesante, pérdida de ingresos,
                oportunidades, reputación o datos, así como por decisiones adoptadas por el Cliente a partir de
                contenidos, informes o salidas de IA de la plataforma, o por actuaciones de proveedores terceros. La
                Compañía no responde de incumplimientos derivados de causas de fuerza mayor, caídas de proveedores de
                infraestructura, cambios normativos o interrupciones de conectividad.
              </p>

              <h2>12. Confidencialidad y uso correcto</h2>
              <p>
                Ambas partes tratarán como confidencial la información no pública a la que accedan con motivo de la
                relación. El Cliente es responsable de la custodia de sus credenciales y de la actividad realizada
                desde su cuenta, que es personal e intransferible. La Compañía podrá suspender o resolver el contrato
                de forma inmediata en caso de uso fraudulento, compartición indebida de accesos, conductas abusivas
                hacia el equipo o impago.
              </p>

              <h2>13. Protección de datos</h2>
              <p>
                El tratamiento de datos personales se rige por la <strong>Política de Privacidad</strong>, que forma
                parte integrante de estas Condiciones. Los datos se tratan y almacenan en Estados Unidos y en otros
                países donde operen los proveedores tecnológicos de la Compañía.
              </p>

              <h2>14. Legislación aplicable (Governing Law)</h2>
              <p>
                Estas Condiciones y cualquier relación contractual derivada del servicio se rigen e interpretan
                <strong> exclusivamente conforme a las leyes del Estado de Delaware, Estados Unidos de América</strong>,
                con exclusión expresa de sus normas de conflicto de leyes, de la Convención de Viena sobre
                Compraventa Internacional de Mercaderías y de cualquier otro convenio internacional que pudiera
                resultar aplicable.
              </p>

              <h2>15. Resolución de conflictos (Binding Arbitration)</h2>
              <p>
                Cualquier controversia, reclamación o disputa relacionada con estas Condiciones, su interpretación,
                ejecución o incumplimiento <strong>no se resolverá ante juzgados o tribunales públicos</strong>. Las
                partes acuerdan someterla a <strong>arbitraje vinculante online</strong>, administrado bajo las reglas
                de un proveedor de arbitraje comercial reconocido, con sede legal y jurisdicción en el
                <strong> Estado de Delaware, USA</strong>, en idioma inglés o español a elección del árbitro. El laudo
                será definitivo y ejecutable. El Cliente renuncia expresamente a participar en acciones colectivas
                (class actions) frente a la Compañía.
              </p>

              <h2>16. Modificaciones, nulidad parcial y versión</h2>
              <p>
                La Compañía podrá actualizar estas Condiciones para reflejar cambios legales, técnicos o de servicio.
                Los cambios sustanciales se notificarán por email o desde el panel con antelación razonable; el uso
                continuado del servicio implicará su aceptación. Si alguna cláusula fuese declarada nula, el resto
                mantendrá plena validez. La versión vigente de estas Condiciones es la <strong>v{TERMS_VERSION}</strong>,
                y la aceptación de cada Cliente se registra con la versión correspondiente.
              </p>

              <p className="text-xs text-muted-foreground mt-10">
                Emisor: {issuer}. Contacto: {LEGAL_ENTITY.email}.
              </p>
            </article>
          ) : (
            <article className="prose prose-lg max-w-none text-foreground font-body leading-relaxed">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                {updated} · v{TERMS_VERSION}
              </p>
              <h1 className="font-display text-4xl font-bold mb-8">Terms of Service</h1>

              <h2>1. Identification and scope</h2>
              <p>
                The service marketed under the <strong>Retail Lemon</strong> brand is operated by
                <strong> WP Media LLC</strong>, a Limited Liability Company organized under the laws of the
                <strong> State of Delaware, United States of America</strong>, with registered address at
                {" "}{LEGAL_ENTITY.address} (the <strong>"Company"</strong>).
              </p>
              <p>
                Services are offered exclusively to professionals, freelancers, companies and legal entities
                contracting within the scope of their economic activity. This is therefore a strictly
                <strong> Business to Business (B2B)</strong> service. By accepting these Terms, the Client represents
                that it acts as a business and not as a consumer.
              </p>

              <h2>2. International service and taxes</h2>
              <p>
                The services are <strong>digital services supplied remotely from the United States</strong> to business
                clients in any country, including, by way of example, markets across the Americas, Europe and the rest of
                the world. The Company has no permanent establishment or tax representation in the Client's country.
              </p>
              <p>
                Invoices are issued from the United States <strong>without local taxes of the Client's country</strong>.
                The Client is solely responsible for its local tax obligations, including, where applicable,
                self-assessment under import-of-services rules or the equivalent mechanism in its jurisdiction (for
                example, the reverse charge in the European Union), and for the accuracy of its tax data and tax ID.
              </p>


              <h2>3. Plans and specific conditions</h2>
              <h3>3.1 Subscription access</h3>
              <p>
                Access to the platform is provided exclusively through one of the three current subscription plans. There
                is no permanent free plan; the Company may enable or withdraw free tiers in the future, which would be
                governed by the conditions published at that time.
              </p>

              <h3>3.2 Paid plans: Basic, Club Pro and Advanced</h3>
              <ul>
                <li>
                  <strong>Basic — €39/month</strong> (annual: €32.50/month, €390 a year): 30 Retail AI Agent queries a
                  month, guides, grants list and limited royalty-free music.
                </li>
                <li>
                  <strong>Club Pro — €69/month</strong> (annual: €57.50/month, €690 a year): everything in Basic, 60
                  queries a month, roadmap, KPIs, full leak detector, matching grants, cases and solutions and 4 AI ad
                  generations a month.
                </li>
                <li>
                  <strong>Advanced — €99/month</strong> (annual: €82.50/month, €990 a year): everything in Club Pro, 100
                  queries a month, online presence, Google Business Profile, automations and 12 AI ad generations a month.
                </li>
                <li>
                  <strong>Annual billing</strong>: 10 monthly fees are paid upfront for 12 months of service (equivalent
                  to 2 months free). The monthly price shown for annual billing is indicative; the amount charged is the
                  annual total.
                </li>
                <li>
                  <strong>7-day free trial</strong> on all three plans, on both monthly and annual billing (one trial per
                  person or business). A payment method is requested at signup, but €0 is charged that day. If the
                  Client does not cancel during the trial, the full amount of the chosen plan and billing period is
                  charged automatically on <strong>day 8</strong>.
                </li>
                <li>
                  <strong>No minimum term</strong>: cancellable in 1 click at any time from the panel; cancellation takes
                  effect at the end of the period already paid (month or year). No refunds for periods already invoiced.
                </li>
              </ul>

              <h3>3.3 Extras (paid separately)</h3>
              <ul>
                <li>
                  Labour module, mentors, licensing procedures, lawyer consultation, commercial music and scent are
                  <strong> not included</strong> in any plan. They are available as paid extras from the Advanced plan,
                  and their price is shown before payment.
                </li>
                <li>No plan includes unlimited lawyer consultations or commercial music.</li>
                <li>Mentoring sessions are remote by video call; no-shows or cancellations with less than 24 hours' notice consume the booked hour.</li>
              </ul>

              <h3>3.4 Add-on modules, extra hours and one-off services</h3>
              <ul>
                <li>Purchased as one-off payments and available immediately after the charge.</li>
                <li>
                  As immediately accessible digital content, they are <strong>non-refundable</strong> once the charge
                  has been processed.
                </li>
              </ul>

              <h2>4. Quotes and customized services</h2>
              <p>
                Quotes issued through the panel (supplier sourcing, furniture, packaging, signage, works, production or
                any bespoke service) correspond to <strong>customized work produced to the Client's
                specifications</strong>.
              </p>
              <ul>
                <li>
                  Accepting a quote in the panel constitutes a <strong>firm, binding and irrevocable order</strong> from
                  the moment of acceptance.
                </li>
                <li>
                  The Client expressly requests and consents to the <strong>immediate start of performance</strong> and
                  waives any right of withdrawal, cancellation or refund, in whole or in part, for customized services.
                </li>
                <li>
                  Stated lead times are <strong>estimates and not of the essence</strong>; they depend on third-party
                  suppliers, manufacturing, shipping and customs.
                </li>
                <li>
                  Third-party prices may change until acceptance; once accepted, the amount is fixed under the terms of
                  the quote.
                </li>
                <li>
                  Product warranties are those offered by the manufacturer or final supplier. The Company acts as an
                  intermediary for sourcing and coordination unless the quote expressly states otherwise.
                </li>
              </ul>
              <p>
                Acceptance is recorded electronically with date, time, version of these Terms and device data, and
                constitutes sufficient evidence of the Client's consent.
              </p>

              <h2>5. Nature of the service: no regulated advice</h2>
              <p>
                Content, modules, templates, checklists, calculators, diagnostics, reports and mentoring sessions are
                provided for <strong>educational and general business guidance</strong> purposes.
              </p>
              <p>
                They <strong>do not constitute legal, tax, accounting, employment, financial, investment, technical,
                planning, health or safety advice</strong>, and do not replace a qualified professional in the Client's
                country. Figures, margins, forecasts and scenarios generated by the platform are estimates based on the
                data entered by the Client.
              </p>
              <p>
                All business decisions and their consequences are the Client's sole responsibility. The Company
                <strong> does not guarantee economic results</strong>, sales growth or profitability.
              </p>
              <p>
                Information about grants and subsidies provided through the platform or by email is
                <strong> informational only</strong>. The Client must always verify it in the corresponding official
                sources (terms, deadlines and requirements). Retail Lemon does not guarantee that any grant, licence or
                subsidy will be obtained.
              </p>


              <h2>6. Artificial intelligence features</h2>
              <p>
                The platform includes AI-powered features (conversational assistant, daily tips, progress summaries,
                automated reports and analyses).
              </p>
              <ul>
                <li>
                  Outputs are generated automatically and <strong>may contain errors, inaccuracies or outdated
                  information</strong>. The Client must verify them before acting.
                </li>
                <li>
                  AI-generated content <strong>does not constitute professional advice</strong> nor a binding statement
                  by the Company, and creates no contractual rights or obligations.
                </li>
                <li>
                  The assistant does not provide suppliers, external contacts or third-party market prices; for that,
                  the Client must use the Quotes section, handled by the human team.
                </li>
                <li>
                  The Client agrees not to enter sensitive personal data, third-party data without a lawful basis, or
                  confidential information belonging to others.
                </li>
                <li>
                  Conversations are stored and may be reviewed by the team for support, quality control and service
                  improvement, as described in the Privacy Policy.
                </li>
              </ul>

              <h2>7. Client content</h2>
              <p>
                The Client retains ownership of the information uploaded to the platform (store photos, documents,
                plans, financial data, notes, messages and attachments) and represents that it holds the necessary
                rights over it.
              </p>
              <ul>
                <li>
                  The Client grants the Company a limited, non-exclusive, revocable licence to host, process and
                  display such content solely to provide the contracted service, generate diagnostics and manage quotes
                  and supplier sourcing.
                </li>
                <li>
                  The Company may use <strong>aggregated, anonymized</strong> data that cannot identify the Client for
                  statistics and product improvement.
                </li>
                <li>
                  The Client shall not upload unlawful or defamatory content, content infringing third-party rights, or
                  malware.
                </li>
                <li>
                  The Company does not guarantee indefinite file retention and is not a backup service. The Client must
                  keep its own copies.
                </li>
              </ul>

              <h2>8. Intellectual property and licence</h2>
              <p>
                The platform, methodology, modules, templates, checklists, reports and other materials are the
                Company's property. The Client receives a <strong>personal, non-transferable, limited licence</strong>
                for internal use in its own business during the subscription term.
              </p>
              <p>
                Resale, assignment, publication, redistribution, use for training third parties, use of the content to
                train or feed artificial intelligence models, and automated data extraction (scraping) are expressly
                prohibited.
              </p>

              <h2>9. Payments, refunds and automatic renewal</h2>
              <h3>9.1 Operating entity and jurisdiction</h3>
              <p>
                The digital services, software tools and mentoring programmes are provided and invoiced by the U.S.
                corporate entity <strong>WP Media LLC</strong>. The engagement is governed by these terms and by the law
                applicable to international commercial transactions.
              </p>

              <h3>9.2 Monthly or annual billing, trial and cancellation</h3>
              <p>
                Plans are billed <strong>monthly or annually</strong>, as chosen by the Client, with <strong>no minimum
                term</strong>. Basic, Club Pro and Advanced include a <strong>7-day free trial</strong> on both billing
                periods (one per person or business); the total amount to be charged is shown before confirming. If the
                user does not cancel during the trial, the first charge is made on <strong>day 8</strong>. The user may
                cancel at any time from the panel; cancellation takes effect at the end of the period already paid, with
                access kept until then.
              </p>

              <h3>9.3 General payment terms</h3>

              <p>
                All payments are processed through Stripe and become final upon successful charge.
                <strong> All sales are final and non-refundable.</strong> As this is a digital B2B service operated
                under U.S. law, the 14-day right of withdrawal provided by European consumer regulations does not
                apply.
              </p>
              <p>
                Subscriptions renew automatically, charging the then-current amount to the payment method on file. It
                is the Client's sole responsibility to cancel from its panel (<strong>panel.retaillemon.com</strong>)
                before the cut-off date. Non-payment entitles the Company to suspend access without notice, without
                prejudice to any outstanding debt.
              </p>

              <h2>10. Mentoring hours rollover and expiration</h2>
              <ul>
                <li><strong>Monthly allocation:</strong> plan hours are granted at the start of each billing cycle.</li>
                <li><strong>Rollover:</strong> unused hours roll over, as a commercial courtesy, to the immediately following cycle.</li>
                <li><strong>Expiration:</strong> rolled-over hours are valid for a maximum of one (1) additional cycle and expire thereafter.</li>
                <li><strong>No refund:</strong> unused or expired hours are not refunded or exchanged for other services.</li>
              </ul>

              <h2>11. Availability, limitation of liability and force majeure</h2>
              <p>
                The service is provided <strong>"as is" and "as available"</strong>, without warranty of uninterrupted
                or error-free operation and without any service level agreement (SLA) unless specifically agreed in
                writing.
              </p>
              <p>
                To the maximum extent permitted by applicable law, the Company's <strong>total aggregate
                liability</strong> to the Client on any ground is limited to the amounts actually paid by the Client in
                the <strong>three (3) months</strong> preceding the event giving rise to the claim.
              </p>
              <p>
                All liability is excluded for indirect damages, loss of profit, revenue, opportunity, reputation or
                data, for decisions taken by the Client based on platform content, reports or AI outputs, and for the
                acts of third-party suppliers. The Company is not liable for failures caused by force majeure,
                infrastructure provider outages, regulatory changes or connectivity interruptions.
              </p>

              <h2>12. Confidentiality and acceptable use</h2>
              <p>
                Both parties shall treat as confidential any non-public information accessed in connection with the
                relationship. The Client is responsible for safeguarding its credentials and for all activity carried
                out from its account, which is personal and non-transferable. The Company may suspend or terminate the
                contract immediately in the event of fraudulent use, improper sharing of access, abusive conduct
                towards the team, or non-payment.
              </p>

              <h2>13. Data protection</h2>
              <p>
                The processing of personal data is governed by the <strong>Privacy Policy</strong>, which forms an
                integral part of these Terms. Data is processed and stored in the United States and in other countries
                where the Company's technology providers operate.
              </p>

              <h2>14. Governing law</h2>
              <p>
                These Terms and any contractual relationship arising from the service shall be governed by and
                construed <strong>exclusively under the laws of the State of Delaware, United States of America</strong>,
                expressly excluding its conflict-of-laws rules, the UN Convention on Contracts for the International
                Sale of Goods and any other international convention that could otherwise apply.
              </p>

              <h2>15. Binding arbitration</h2>
              <p>
                Any controversy, claim or dispute relating to these Terms — including their interpretation, performance
                or breach — <strong>shall not be resolved before public courts</strong>. The parties agree to submit it
                to <strong>binding online arbitration</strong>, administered under the rules of a recognized commercial
                arbitration provider, with legal seat and jurisdiction in the <strong>State of Delaware, USA</strong>,
                conducted in English or Spanish at the arbitrator's discretion. The award shall be final and
                enforceable. The Client expressly waives any right to participate in class actions against the Company.
              </p>

              <h2>16. Modifications, severability and version</h2>
              <p>
                The Company may update these Terms to reflect legal, technical or service changes. Material changes
                will be communicated by email or through the panel with reasonable notice; continued use of the service
                constitutes acceptance. If any clause is held invalid, the remainder shall remain in full force. The
                current version of these Terms is <strong>v{TERMS_VERSION}</strong>, and each Client's acceptance is
                recorded together with the applicable version.
              </p>

              <p className="text-xs text-muted-foreground mt-10">
                Issued by {issuer}. Contact: {LEGAL_ENTITY.email}.
              </p>
            </article>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
};

export default TermsOfService;
